import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Field, Area, SelectField, Card } from '@/admin/fields';

const LINE_STYLE_OPTIONS = [
  { value: 'ink', label: 'Ink (default)' },
  { value: 'green', label: 'Dark green' },
  { value: 'purple', label: 'Purple' },
  { value: 'soft', label: 'Soft italic (small)' },
];

const WAY_STYLE_OPTIONS = [
  { value: 'white', label: 'White' },
  { value: 'dim', label: 'Dimmed white' },
  { value: 'purple', label: 'Light purple' },
];

export function BrandTab({ content, update }) {
  return (
    <Card title="Brand basics" hint="The name powers the animated wordmark — every letter U keeps the purple ⇄ green transition.">
      <Field label="Brand name" value={content.brand.name} onChange={(v) => update('brand.name', v)} testId="edit-brand-name" />
      <Field label="Tagline" value={content.brand.tagline} onChange={(v) => update('brand.tagline', v)} testId="edit-brand-tagline" />
      <Area label="Brand intro" rows={2} value={content.brand.intro} onChange={(v) => update('brand.intro', v)} testId="edit-brand-intro" />
      <Field label="Nav label — Brand" value={content.nav.brand} onChange={(v) => update('nav.brand', v)} testId="edit-nav-brand" />
      <Field label="Nav label — Drop" value={content.nav.drop} onChange={(v) => update('nav.drop', v)} testId="edit-nav-drop" />
      <Field label="Nav label — Collection" value={content.nav.collection} onChange={(v) => update('nav.collection', v)} testId="edit-nav-collection" />
      <Field label="Nav label — Contact" value={content.nav.contact} onChange={(v) => update('nav.contact', v)} testId="edit-nav-contact" />
    </Card>
  );
}

export function HeroTab({ content, update }) {
  return (
    <Card title="Homepage hero" hint="One line per row. The last tagline line is highlighted in purple.">
      <Field label="Mirrored reflection word" value={content.hero.reflection} onChange={(v) => update('hero.reflection', v)} testId="edit-hero-reflection" />
      <Area
        label="Hero tagline lines"
        rows={3}
        value={content.hero.lines.join('\n')}
        onChange={(v) => update('hero.lines', v.split('\n').filter((l) => l.trim()))}
        testId="edit-hero-lines"
      />
      <Field label="Button label" value={content.hero.cta} onChange={(v) => update('hero.cta', v)} testId="edit-hero-cta" />
      <Area
        label="Bottom bar labels"
        rows={4}
        hint="One per row — e.g. EDITION: DROP 001"
        value={content.hero.meta.join('\n')}
        onChange={(v) => update('hero.meta', v.split('\n').filter((l) => l.trim()))}
        testId="edit-hero-meta"
      />
    </Card>
  );
}

export function MarqueeTab({ content, update }) {
  return (
    <Card title="Marquee strip" hint="The scrolling strip under the hero. One phrase per row.">
      <Area
        label="Marquee phrases"
        rows={5}
        value={content.marquee.join('\n')}
        onChange={(v) => update('marquee', v.split('\n').filter((l) => l.trim()))}
        testId="edit-marquee"
      />
    </Card>
  );
}

export function StatementTab({ content, update }) {
  const lines = content.statement.lines;
  const setLine = (i, key, v) => {
    const next = lines.map((l, idx) => (idx === i ? { ...l, [key]: v } : l));
    update('statement.lines', next);
  };
  return (
    <div className="space-y-6">
      <Card title="Statement header & diagram">
        <Field label="Eyebrow" value={content.statement.eyebrow} onChange={(v) => update('statement.eyebrow', v)} testId="edit-statement-eyebrow" />
        <Field label="Diagram top" value={content.statement.diagram_top} onChange={(v) => update('statement.diagram_top', v)} testId="edit-diagram-top" />
        <Field label="Diagram middle" value={content.statement.diagram_middle} onChange={(v) => update('statement.diagram_middle', v)} testId="edit-diagram-middle" />
        <Field label="Diagram bottom" value={content.statement.diagram_bottom} onChange={(v) => update('statement.diagram_bottom', v)} testId="edit-diagram-bottom" />
      </Card>
      <Card title="Statement lines" hint="Each line can carry its own color treatment.">
        {lines.map((line, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-3 items-start border-b border-yunti-ink/10 pb-4">
            <div className="flex-1 w-full">
              <Field label={`Line ${i + 1}`} value={line.text} onChange={(v) => setLine(i, 'text', v)} testId={`edit-statement-line-${i}`} />
            </div>
            <div className="w-full sm:w-48">
              <SelectField label="Style" value={line.style} onChange={(v) => setLine(i, 'style', v)} options={LINE_STYLE_OPTIONS} testId={`edit-statement-style-${i}`} />
            </div>
            <button
              type="button"
              onClick={() => update('statement.lines', lines.filter((_, idx) => idx !== i))}
              className="text-xs font-mono uppercase text-red-700 hover:underline mt-6"
              data-testid={`remove-statement-line-${i}`}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => update('statement.lines', [...lines, { text: 'New line', style: 'ink' }])}
          className="px-4 py-2 border border-yunti-ink text-xs font-mono uppercase tracking-widest hover:bg-yunti-ink hover:text-white transition-colors"
          data-testid="add-statement-line"
        >
          + Add line
        </button>
      </Card>
    </div>
  );
}

export function WayTab({ content, update }) {
  const lines = content.theWay.lines;
  const setLine = (i, key, v) => update('theWay.lines', lines.map((l, idx) => (idx === i ? { ...l, [key]: v } : l)));
  return (
    <Card title="The YUNTI Way" hint="The dark green statement section.">
      <Field label="Badge" value={content.theWay.badge} onChange={(v) => update('theWay.badge', v)} testId="edit-way-badge" />
      {lines.map((line, i) => (
        <div key={i} className="flex flex-col sm:flex-row gap-3 items-start border-b border-yunti-ink/10 pb-4">
          <div className="flex-1 w-full">
            <Field label={`Line ${i + 1}`} value={line.text} onChange={(v) => setLine(i, 'text', v)} testId={`edit-way-line-${i}`} />
          </div>
          <div className="w-full sm:w-48">
            <SelectField label="Style" value={line.style} onChange={(v) => setLine(i, 'style', v)} options={WAY_STYLE_OPTIONS} testId={`edit-way-style-${i}`} />
          </div>
        </div>
      ))}
      <Area
        label="Token chain"
        rows={2}
        hint="One token per row — rendered as [ UNITY ] ⇄ [ PERSPECTIVE ] ⇄ [ UNIQUE ]"
        value={content.theWay.tokens.join('\n')}
        onChange={(v) => update('theWay.tokens', v.split('\n').filter((l) => l.trim()))}
        testId="edit-way-tokens"
      />
    </Card>
  );
}

export function ContactLinksTab({ content, update }) {
  return (
    <div className="space-y-6">
      <Card title="Contact section" hint="The email shows as a link under the heading when set. Leave empty to hide it.">
        <Field label="Eyebrow" value={content.contact.eyebrow} onChange={(v) => update('contact.eyebrow', v)} testId="edit-contact-eyebrow" />
        <Field label="Heading" value={content.contact.title} onChange={(v) => update('contact.title', v)} testId="edit-contact-title" />
        <Area label="Supporting text" rows={2} value={content.contact.support} onChange={(v) => update('contact.support', v)} testId="edit-contact-support" />
        <Field label="Contact email" value={content.contact.email} onChange={(v) => update('contact.email', v)} placeholder="hello@yunti.com" testId="edit-contact-email" />
      </Card>
      <Card title="Footer & social" hint="Instagram stays a placeholder until you paste the real profile URL.">
        <Field label="Instagram label" value={content.footer.instagram} onChange={(v) => update('footer.instagram', v)} testId="edit-footer-instagram" />
        <Field label="Instagram URL" value={content.footer.instagramUrl} onChange={(v) => update('footer.instagramUrl', v)} placeholder="https://instagram.com/yunti" testId="edit-footer-instagram-url" />
        <Field label="Footer — Shop label" value={content.footer.shop} onChange={(v) => update('footer.shop', v)} testId="edit-footer-shop" />
        <Field label="Footer — Contact label" value={content.footer.contact} onChange={(v) => update('footer.contact', v)} testId="edit-footer-contact" />
        <Field label="Copyright line" value={content.footer.copyright} onChange={(v) => update('footer.copyright', v)} testId="edit-footer-copyright" />
        <Field label="Collection line" value={content.footer.collection} onChange={(v) => update('footer.collection', v)} testId="edit-footer-collection" />
      </Card>
    </div>
  );
}

export function MessagesTab() {
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState('');

  const load = () =>
    api
      .get('/admin/messages')
      .then((r) => setMessages(r.data.messages))
      .catch((e) => setError(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (m) => {
    try {
      await api.put(`/admin/messages/${m.id}/read`, { read: !m.read });
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm('Remove this message from the inbox?')) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <Card title="Contact form messages" hint="Every message sent through the site form lands here and is also emailed to you.">
      {error && <p className="text-xs font-mono text-red-700">{error}</p>}
      {messages && messages.length === 0 && <p className="text-sm text-yunti-ink/50">No messages yet.</p>}
      {messages &&
        messages.map((m) => (
          <div key={m.id} className={`border-b border-yunti-ink/10 pb-3 ${m.read ? 'opacity-60' : ''}`} data-testid={`message-${m.id}`}>
            <div className="flex flex-wrap justify-between gap-2 text-xs font-mono text-yunti-ink/60">
              <span className="font-semibold text-yunti-ink">
                {!m.read && <span className="text-yunti-purple mr-1">●</span>}
                {m.name}
              </span>
              <a href={`mailto:${m.email}`} className="text-yunti-purple">{m.email}</a>
              <span>{m.created_at?.slice(0, 10)}</span>
            </div>
            <p className="text-sm text-yunti-ink/80 mt-1 whitespace-pre-wrap">{m.message}</p>
            <div className="flex gap-4 mt-2">
              <button
                type="button"
                onClick={() => toggleRead(m)}
                className="text-[11px] font-mono uppercase tracking-wider text-yunti-ink/60 hover:text-yunti-ink"
                data-testid={`message-read-${m.id}`}
              >
                {m.read ? 'Mark unread' : 'Mark read'}
              </button>
              <button
                type="button"
                onClick={() => remove(m)}
                className="text-[11px] font-mono uppercase tracking-wider text-red-700 hover:underline"
                data-testid={`message-delete-${m.id}`}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
    </Card>
  );
}
